import { props, state } from 'cerebral'
import { debounce, merge, set } from 'cerebral/factories'
import page from 'page'
import qs from 'qs'
import { isNil, omit, reject, values } from 'ramda'

let app

const routeConfig = [
  ['toRoot', '/'],
  ['toLogin', '/login/:token', { navShown: false }],
  ['toAlbums', '/albums/list/:filter?', { packageRequired: 'albums' }],
  ['toAlbum', '/albums/:id/:action?', { packageRequired: 'albums' }],
  ['toAlerts', '/alerts/list/:filter?', { packageRequired: 'alerts', params: { $sort: { postedAt: -1 } } }],
  ['toAlert', '/alerts/:id/:action?', { packageRequired: 'alerts' }],
  ['toAwards', '/appreciation-awards/list/:recipientProfile?', { packageRequired: 'staff-appreciation', params: { filter: 'new', $sort: { createdAt: -1 } } }],
  ['toAward', '/appreciation-awards/:id/:action?', { packageRequired: 'staff-appreciation' }],
  ['toAppointments', '/appointments/list/:filter?', { packageRequired: 'appointments' }],
  [
    'toAppointmentCalendar',
    '/appointments/calendar/:action?',
    { packageRequired: 'appointments', params: { eventType: 'Appointment', filter: 'published' }, storePath: 'appointments.lastListView' },
  ],
  ['toAppointment', '/appointments/:id/:action?', { packageRequired: 'appointments' }],
  ['toCarpools', '/carpools/list/:filter?', { packageRequired: 'carpools', params: { $sort: { createdAt: -1 } }, filter: 'building' }],
  ['toCarpool', '/carpools/:id/:action?', { packageRequired: 'carpools' }],
  ['toCapacityLocations', '/capacity-locations/list/:filter?', { packageRequired: 'capacity', params: { $sort: { postedAt: -1 } } }],
  ['toCapacityLocation', '/capacity-locations/:id/:action?', { packageRequired: 'capacity' }],
  ['toCalendar', '/calendars/:id/:action?', { packageRequired: 'events', params: {} }],
  ['toContactInfo', '/contact-info/:id/:action?', { params: {}, packageRequired: 'people' }],
  ['toContactInfoSettings', '/contact-info-settings', { packageRequired: 'people' }],
  ['toContactSettings', '/contact-settings', { packageRequired: 'people' }],
  ['toCustomReports', '/reports/:id?', { packageRequired: 'custom-reports' }],
  ['toDisplays', '/displays', {}],
  ['toDisplay', '/displays/:id', {}],
  [
    'toEvents',
    '/events/list/:filter?',
    { packageRequired: 'events', params: { eventType: 'Event', $sort: { startAt: -1 } }, storePath: 'events.lastListView' },
  ],
  [
    'toEventCalendar',
    '/events/calendar/:action?',
    { packageRequired: ['events', 'rsvps'], params: { eventType: 'Event', filter: 'published' }, storePath: 'events.lastListView' },
  ],
  [
    'toEventReport',
    '/events/report/:action?',
    { packageRequired: 'events', params: { eventType: 'Event', filter: 'report' }, storePath: 'events.lastListView' },
  ],
  ['toEvent', '/events/:id/:action?', { packageRequired: ['events', 'rsvps'], params: { eventType: 'Event' } }],
  ['toEventAttendances', '/event-attendances', { packageRequired: 'attendance', params: {} }],
  ['toEventAttendance', '/event-attendances/:id', { packageRequired: 'attendance' }],
  ['toEventLocations', '/event-locations', { packageRequired: 'events' }],
  ['toEventLocation', '/event-locations/:id', { packageRequired: 'events' }],
  ['toEventRsvps', '/event-rsvps', { packageRequired: ['events', 'rsvps'], params: {} }],
  ['toEventRsvp', '/event-rsvps/:id', { packageRequired: ['events', 'rsvps'] }],
  ['toEventInstancesSummary', '/event-instances/summary', { packageRequired: 'attendance', params: {} }],
  ['toEventInstances', '/event-instances/list/:filter', { packageRequired: 'attendance', params: { $sort: { date: -1 } } }],
  ['toGeofenceRegions', '/geofence-regions/list/:filter?', { filter: 'active' }],
  ['toGeofenceRegion', '/geofence-regions/:id', {}],
  ['toInvitationsMerge', '/invitations/merge', { packageRequired: 'people' }],
  ['toInvitations', '/invitations/list/:filter?', { packageRequired: 'people', params: { $sort: { invitedAt: -1 } } }],
  ['toInvitation', '/invitations/:id', { packageRequired: 'people' }],
  ['toLibraryItems', '/content-library/list/:provider?', { packageRequired: 'content-library', params: { $sort: { title: 1 } } }],
  ['toLibraryItem', '/content-library/:id', { packageRequired: 'content-library' }],
  ['toLifts', '/lifts', { packageRequired: 'lifts', params: { $sort: { name: 1 } } }],
  ['toLift', '/lifts/:id', { packageRequired: 'lifts' }],
  ['toLocationVehicles', '/vehicles/list/:filter?', { packageRequired: 'vehicles' }],
  ['toLocationVehicleView', '/vehicles/:id/view/:tab?', { packageRequired: 'vehicles' }],
  ['toLocationVehicle', '/vehicles/:id/:action?', { packageRequired: 'vehicles' }],
  ['toLocationVehicleDrivers', '/vehicle-drivers/list/:filter?', { packageRequired: 'people' }],
  ['toLocationVehicleMaintenanceSchedule', '/vehicles-maintenance-schedule/:id/:action?', { packageRequired: 'vehicles' }],
  ['toLocationVehicleShifts', '/vehicle-shifts/list/:filter?', { packageRequired: 'vehicles' }],
  ['toLocationRoutes', '/routes/list/:filter?', { packageRequired: 'vehicles' }],
  ['toLocationRouteView', '/routes/:id/view', { packageRequired: 'vehicles' }],
  ['toLocationRoute', '/routes/:id/:action?', { packageRequired: 'vehicles' }],
  ['toLocationRouteRequests', '/route-requests/list/:filter?', { packageRequired: 'vehicles', params: { $sort: { createdAt: 1 } } }],
  ['toLocationRouteRequestView', '/route-requests/:id/view', { packageRequired: 'vehicles' }],
  ['toLocationRouteRequest', '/route-requests/:id/:action?', { packageRequired: 'vehicles' }],
  ['toLocationRouteDispatchRequests', '/route-dispatch-requests/list/:locationRouteRequest?', { packageRequired: 'vehicles' }],
  ['toMapPoints', '/map-points/list/:filter?', { packageRequired: 'map-points' }],
  ['toMapPoint', '/map-points/:id/:action?', { packageRequired: 'map-points' }],
  ['toMapAreas', '/map-areas/list/:filter?', { packageRequired: 'map-points' }],
  ['toMapArea', '/map-areas/:id/:action?', { packageRequired: 'map-points' }],
  ['toMapCategories', '/map-categories/list/:filter?', { packageRequired: 'map-points' }],
  ['toMapCategorie', '/map-categories/:id/:action?', { packageRequired: 'map-points' }],
  [
    'toMealsCalendar',
    '/meals/calendar/:action?',
    { packageRequired: 'menu', params: { eventType: 'Menu', filter: 'published', $limit: 250 }, storePath: 'events.lastListView' },
  ],
  [
    'toMealsReport',
    '/meals/report/:action?',
    { packageRequired: 'menu', params: { eventType: 'Menu', filter: 'report', $limit: 250 }, storePath: 'events.lastListView' },
  ],
  ['toMealsUpload', '/meals/upload', { packageRequired: 'menu' }],
  ['toMealsSync', '/meals/sync', { packageRequired: 'menu' }],
  ['toDiningRdForm', '/meals/diningrd', { packageRequired: 'menu' }],
  ['toMeals', '/meals/list/:filter?', { packageRequired: 'menu', params: { eventType: 'Menu', $sort: { startAt: 1 } }, storePath: 'events.lastListView' }],
  ['toMeal', '/meals/:id/:action?', { packageRequired: 'menu', params: { eventType: 'Menu' } }],
  ['toMealLocation', '/meal-location/:id/:action?', { packageRequired: 'menu', params: { eventType: 'Menu' } }],
  ['toMedias', '/media/list/:filter?', { packageRequired: 'media' }],
  ['toMedia', '/media/:id/:action?', { packageRequired: 'media' }],
  ['toMobileWelcomes', '/welcomes/list/:filter?', { packageRequired: 'notifications' }],
  ['toMobileWelcome', '/welcomes/:id/:action?', { packageRequired: 'notifications' }],
  ['toNotifications', '/notifications/list/:filter?', { packageRequired: 'notifications' }],
  ['toNotificationView', '/notifications/:id/view', { packageRequired: 'notifications' }],
  ['toNotification', '/notifications/:id/:action?', { packageRequired: 'notifications' }],
  ['toOrganization', '/organization', {}],
  ['toOneToOneActivities', '/one-to-one-activities', { packageRequired: 'attendance', params: {} }],
  ['toOneToOneActivity', '/one-to-one-activity/:id', { packageRequired: 'attendance' }],
  ['toPeople', '/people/list/:userTypes?', { packageRequired: ['people', 'rsvps'], params: { $sort: { 'name.last': 1 } } }],
  ['toPerson', '/people/:id', { packageRequired: ['people', 'rsvps'], params: { attendanceForProfile: true } }],
  ['toPlaylists', '/playlists', {}],
  ['toPlaylist', '/playlists/:id', {}],
  ['toPrices', '/prices', { packageRequired: 'pricing' }],
  ['toPrice', '/prices/:id', { packageRequired: 'pricing' }],
  ['toPricings', '/pricings', { packageRequired: 'pricing' }],
  ['toPricing', '/pricings/:id', { packageRequired: 'pricing' }],
  ['toPrintOrder', '/print-orders/:id', { packageRequired: 'print-orders' }],
  ['toPrintOrders', '/print-orders/list/:filter?', { packageRequired: 'print-orders', params: { $sort: { createdAt: 1 } } }],
  ['toProfile', '/profiles/:id', { packageRequired: 'people' }],
  ['toPublications', '/publications/list/:filter?', { packageRequired: 'publications' }],
  ['toPublication', '/publications/:id/:action?', { packageRequired: 'publications' }],
  ['toReservations', '/reservations/list/:filter?', { packageRequired: 'reservations' }],
  ['toReservation', '/reservations/:id?', { packageRequired: 'reservations' }],
  ['toResources', '/resources/list/:resourceGroup?', { packageRequired: 'resources' }],
  ['toResource', '/resources/:id?', { packageRequired: 'resources' }],
  ['toRewards', '/rewards/list', { packageRequired: 'carpools' }],
  ['toReward', '/rewards/:id?', { packageRequired: 'carpools' }],
  ['toQuests', '/quests/list', { packageRequired: 'carpools' }],
  ['toQuest', '/quests/:id?', { packageRequired: 'carpools' }],
  ['toQuestCompletions', '/quest-completions/list', { packageRequired: 'carpools' }],
  ['toQuestCompletion', '/quest-completions/:id?', { packageRequired: 'carpools' }],
  ['toRewardRedemptions', '/reward-redemptions/list/:filter?', { packageRequired: 'carpools', params: { $sort: { createdAt: -1 } } }],
  ['toRewardRedemption', '/reward-redemptions/:id', { packageRequired: 'carpools' }],
  ['toRideProducts', '/ride-products/list', { packageRequired: 'ride-products' }],
  ['toRideProduct', '/ride-products/:id?', { packageRequired: 'ride-products' }],
  ['toRideProductSubscriptions', '/ride-product-subscriptions:', { packageRequired: 'ride-products' }],
  ['toSettings', '/settings', {}],
  ['toMobileSettings', '/settings/mobile', { packageRequired: 'notifications' }],
  ['toEventSettings', '/settings/event', { packageRequired: 'events' }],
  ['toServiceRequests', '/service-requests/list/:filter?', { packageRequired: 'service-requests' }],
  ['toSignageSettings', '/settings/signage', {}],
  ['toSnowReports', '/snow-reports/list/:filter?', { packageRequired: 'snow-reports' }],
  ['toSnowReport', '/snow-reports/:id/:action?', { packageRequired: 'snow-reports' }],
  ['toSnowStats', '/snow-stats', { packageRequired: 'snow-stats' }],
  ['toStatusUpdates', '/status-updates/list', { params: { $sort: { createdAt: -1 } } }],
  ['toSurveys', '/surveys/list/:filter?', { packageRequired: 'surveys' }],
  ['toSurvey', '/surveys/:id', { packageRequired: 'surveys' }],
  ['toSurveyResponses', '/survey-responses/:survey?', { packageRequired: 'surveys' }],
  ['toTrails', '/trails', { packageRequired: 'trails', params: { $sort: { name: 1 } } }],
  ['toTrail', '/trails/:id', { packageRequired: 'trails' }],
  ['toUpdates', '/updates/list/:filter?', { packageRequired: 'updates', params: { $sort: { postedAt: -1 } } }],
  ['toUpdate', '/updates/:id/:action?', { packageRequired: 'updates' }],
]

page('*', (ctx, next) => {
  ctx.query = qs.parse(ctx.querystring)
  next()
})

page.redirect('/albums', '/albums/list')
page.redirect('/albums/list', '/albums/list/today')
page.redirect('/alerts', '/alerts/list/active')
page.redirect('/alerts/list', '/alerts/list/active')
page.redirect('/appointments/list', '/appointments/list/upcoming')
page.redirect('/carpools/list', '/carpools/list/building')
page.redirect('/capacities', '/capacities/list')
page.redirect('/content-library', '/content-library/list')
page.redirect('/invitations', '/invitations/list')
page.redirect('/events', '/events/list/upcoming')
page.redirect('/events/list', '/events/list/upcoming')
page.redirect('/geofence-regions', '/geofence-regions/list')
page.redirect('/geofence-regions/list', '/geofence-regions/list/active')
page.redirect('/media', '/media/list')
page.redirect('/media/list', '/media/list/active')
page.redirect('/meals', '/meals/list/upcoming')
page.redirect('/meals/list', '/meals/list/upcoming')
page.redirect('/notifications', '/notifications/list/active')
page.redirect('/notifications/list', '/notifications/list/active')
page.redirect('/route-requests/list', '/route-requests/list/requesting')
page.redirect('/print-orders/list', '/print-orders/list/draft')
page.redirect('/reward-redemptions/list', '/reward-redemptions/list/requested')
page.redirect('/reservations', '/reservations/list/upcoming')
page.redirect('/reservations/list', '/reservations/list/upcoming')
page.redirect('/people', '/people/list')
page.redirect('/service-requests/list', '/service-requests/list/pending')
page.redirect('/surveys/list', '/surveys/list/active')
page.redirect('/updates', '/updates/list/active')
page.redirect('/updates/list', '/updates/list/active')
page.redirect('/welcomes/list', '/welcomes/list/draft')

const paramRE = /\/:[a-z_]+\??/gi
// Factory for matching URL patterns to ad hoc sequences.
function route(url, sequence, key) {
  page(url, ({ path, params, query }) => app.runSequence(key, sequence, { params, query, path }))

  const tokens = (url.match(paramRE) || []).reduce((result, token) => {
    const name = token.slice(2, token.endsWith('?') ? -1 : undefined)
    return { ...result, [name]: token }
  }, {})
  const tokenNames = Object.keys(tokens)
  const omitTokenNames = omit(tokenNames)

  const toHref = (params = {}) => {
    let currentUrl = url
    const extraParams = omitTokenNames(params)

    tokenNames.forEach((name) => {
      const token = tokens[name]
      const value = params[name] || ''
      currentUrl = currentUrl.replace(token, value ? `/${value}` : '')
    })

    // Add other parameters as query string.
    if (values(extraParams).filter((v) => v !== undefined && v !== null).length) {
      currentUrl = `${currentUrl}?${qs.stringify(extraParams)}`
    }

    return currentUrl
  }

  const action = ({ props: { params = {} } }) => {
    page.show(toHref(params))
  }

  return { action, toHref }
}

const restoreSavedParams = (routeKey) => (options) => {
  const { props: { path: routePath } = {}, get, store } = options
  const { restoring, path, search, sort } = get(state`ui.listViews.${routeKey}`) || {}

  if (restoring && path === routePath) {
    store.merge(state`route.params`, reject(isNil, { $search: search, $sort: sort }))
  } else if (restoring) {
    store.set(state`ui.listViews.${routeKey}.restoring`, false)
  }
}

const markListViewRestoring = ({ props: { listKey }, get, store }) => {
  const current = get(state`ui.listViews.${listKey}`)
  store.set(state`ui.listViews.${listKey}`, { ...current, restoring: true })
}

let visibleBackToList = null

export const setVisibleBackToList = (backToList) => {
  visibleBackToList = backToList
}

const onBrowserBack = () => {
  const { listKey, listPath } = visibleBackToList || {}
  if (listPath && window.location.pathname + window.location.search === listPath) {
    app.getSequence('route.markListViewRestoring')({ listKey })
  }
}

// Set state for path and params.
const createSequence = (key, { navShown = true, navOffscreen = false, params = {}, storePath } = {}) =>
  [
    storePath && set(state`${storePath}`, props`path`),
    merge(state`route.last`, {
      key: state`route.key`,
      path: state`route.path`,
    }),
    merge(state`route`, {
      navShown,
      navOffscreen,
      key,
      path: props`path` || '/',
      params: props`params` || {},
      query: props`query` || {},
    }),
    // Override with configured params for route.
    Object.keys(params).length > 0 && merge(state`route.params`, params),
    restoreSavedParams(key),
  ].filter(Boolean)

// Keys are functions taking params and returning paths.
export const routePaths = {}
// Keys are functions taking packagesEnabled and returning access boolean.
export const routeAccess = {}

const routes = routeConfig.reduce((result, [key, path, options = {}]) => {
  const sequence = createSequence(key, options)
  const { action, toHref } = route(path, sequence, key)
  const { packageRequired } = options
  routePaths[key] = toHref
  routeAccess[key] = packageRequired ? (packages) => [].concat(packageRequired).some((slug) => packages[slug]) : () => true
  return { ...result, [key]: action }
}, {})

export const sequences = {
  toPath: ({ props: { path, query } }) => {
    let queryString = ''
    const [pathOnly, pathQueryString] = path.split('?')
    // Allow query prop to modify path.
    if (query) {
      if (pathQueryString) {
        query = { ...qs.parse(pathQueryString), ...query }
        queryString = qs.stringify(query)
      }
      queryString = '?' + queryString
    }
    page.show(pathOnly + queryString)
  },
  ...routes,
  setSort: set(state`route.params.$sort`, props`sort`),
  setSearch: [
    debounce(250),
    {
      continue: set(state`route.params.$search`, props`search`),
      discard: [],
    },
  ],
  // Use with care for "filter", as this may interfere with routes which use the filter param.
  setParam: set(state`route.params.${props`key`}`, props`value`),
  setParams: set(state`route.params`, props`params` || {}),
  // Go back to a saved list and have it restore its page, search and sort.
  toSavedListView: [
    //
    markListViewRestoring,
    ({ props: { href } }) => {
      page.show(href)
    },
  ],
  markListViewRestoring: [
    //
    markListViewRestoring,
  ],
}

export default ({ app: configuredApp }) => {
  app = configuredApp
  app.on('initialized', () => {
    window.addEventListener('popstate', onBrowserBack)
    page.start()
  })

  return {
    state: {
      navShown: false,
      path: '/',
      key: 'toLogin',
      params: {},
      last: {},
    },
    sequences,
  }
}
